import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { authApi } from '../../api/auth.api';
import { readRemember, startSession, writeRemember } from '../../model/session';
import { problemMessage, toProblem } from '@/shared/api/problem';
import { Button } from '@/shared/ui/atoms/Button';
import { Checkbox } from '@/shared/ui/atoms/Checkbox';
import { FontIcon } from '@/shared/ui/atoms/FontIcon';
import { Input } from '@/shared/ui/atoms/Input';
import { FormField } from '@/shared/ui/molecules/FormField';
import { PasswordInput } from '@/shared/ui/molecules/PasswordInput';
import '../AuthForm.styl';

const schema = z.object({
  email: z.string().trim().email('invalidEmail'),
  password: z.string().min(1, 'requiredPassword')
});

export function LoginForm() {
  const { t } = useTranslation(undefined, { keyPrefix: 'auth.login' });
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(readRemember);
  const [error, setError] = useState<string | null>(null);

  const login = useMutation({
    mutationFn: (v: z.infer<typeof schema>) => authApi.login(v.email, v.password),
    onSuccess: (res) => {
      writeRemember(remember);
      const home = startSession(res);
      const from = location.state?.from;
      navigate(from && from !== '/login' && res.user.role !== 'PLATFORM_ADMIN' ? from : home, { replace: true });
    },
    onError: (err) => {
      setError(problemMessage(toProblem(err), t('failed')));
    }
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = schema.safeParse({ email, password });
    if (!parsed.success) {
      const key = parsed.error.issues[0]?.message === 'requiredPassword' ? 'requiredPassword' : 'invalidEmail';
      setError(t(key));
      return;
    }
    login.mutate(parsed.data);
  };

  return (
    <form className="f-auth" onSubmit={submit} noValidate>
      <div className="f-auth__fields f-auth__fields--labelled">
        <FormField label={t('email')} htmlFor="login-email">
          <Input
            id="login-email"
            type="email"
            name="email"
            autoComplete="email"
            placeholder={t('emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            invalid={!!error && error === t('invalidEmail')}
            leading={<FontIcon name="envelop2" />}
          />
        </FormField>
        <FormField label={t('password')} htmlFor="login-password">
          <PasswordInput
            id="login-password"
            name="password"
            autoComplete="current-password"
            placeholder="••••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </FormField>
      </div>
      <div className="f-auth__row">
        <Checkbox checked={remember} onChange={setRemember} label={t('remember')} />
        <Link className="f-auth__link" to="/forgot-password">
          {t('forgot')}
        </Link>
      </div>
      {error ? (
        <div className="f-auth__alert" role="alert">
          {error}
        </div>
      ) : null}
      <Button type="submit" block className="f-auth__submit" loading={login.isPending}>
        {t('submit')}
      </Button>
    </form>
  );
}
